import moment from "moment";
import * as Yup from "yup";
import { samePuntoAddress } from "./fptTraslados/fptTrasladosUtils";

export const iamiValidation = ({ fechaPrimeraCuracionMinDate, isView }) =>
  Yup.object({
    fechaHoraAccidente: Yup.date()
      .nullable()
      .typeError("Fecha inválida")
      .max(
        new Date(Date.now()),
        "La fecha de accidente no puede ser mayor a la fecha actual",
      ),

    fechaHoraAbondonoTrabajo: Yup.date()
      .nullable()
      .max(
        new Date(Date.now()),
        "La fecha de abandono de trabajo no puede ser mayor a la fecha actual",
      )
      .typeError("Fecha inválida")
      .test({
        name: "minFechaHoraAccidente",
        message:
          "La fecha en que abandonó el trabajo debe ser mayor o igual a la fecha de accidente",
        test: function (value) {
          return this.parent.fechaHoraAccidente && value
            ? value >=
            new Date(this.parent.fechaHoraAccidente).setHours(0, 0, 0, 0)
            : true;
        },
      }),

    fechaPrimeraCuracion: Yup.date()
      .nullable()
      .when([], {
        is: () => !isView,
        then: (schema) =>
          schema
            .max(
              new Date(Date.now()),
              "La fecha de primera curación no puede ser mayor a la fecha actual",
            )
            .typeError("Fecha inválida")
            .test({
              name: "minFechaHoraAccidente",
              message:
                "La fecha de primera curación debe ser mayor o igual a la fecha de accidente",
              test: function (value) {
                return this.parent.fechaHoraAccidente && value
                  ? value >=
                  new Date(this.parent.fechaHoraAccidente).setHours(
                    0,
                    0,
                    0,
                    0,
                  )
                  : true;
              },
            })
            .test({
              name: "minFechaPrimeraCuracion",
              message: `La fecha de primera curación debe ser mayor a ${moment(
                fechaPrimeraCuracionMinDate,
              ).format("DD/MM/YYYY")}`,
              test: function (value) {
                return fechaPrimeraCuracionMinDate
                  ? value > new Date(fechaPrimeraCuracionMinDate)
                  : true;
              },
            }),
      }),

    fechaHoraProximaConsulta: Yup.date()
      .nullable()
      .when([], {
        is: () => !isView,
        then: (schema) =>
          schema
            .min(
              new Date(Date.now()),
              "La fecha de próxima consulta no puede ser menor a la fecha actual",
            )
            .typeError("Fecha inválida")
            .test({
              name: "minFechaHoraAccidente",
              message:
                "La fecha de próxima consulta debe ser mayor a la fecha de accidente",
              test: function (value) {
                return this.parent.fechaHoraAccidente && value
                  ? value > this.parent.fechaHoraAccidente
                  : true;
              },
            })
            .test({
              name: "minFechaPrimeraCuracion",
              message:
                "La fecha de próxima consulta debe ser mayor a la fecha de primera curación",
              test: function (value) {
                return this.parent.fechaPrimeraCuracion && value
                  ? new Date(value).setHours(0, 0, 0, 0) >
                  this.parent.fechaPrimeraCuracion
                  : true;
              },
            }),
      }),

    fechaAtencionReagravacion: Yup.date()
      .nullable()
      .typeError("Fecha inválida")
      .test({
        name: "required",
        message: "Campo obligatorio",
        test: function (value) {
          return this.parent.reagravacion ? value : true;
        },
      }),

    fechaReagravacion: Yup.date()
      .nullable()
      .typeError("Fecha inválida")
      .test({
        name: "required",
        message: "Campo obligatorio",
        test: function (value) {
          return this.parent.reagravacion ? value : true;
        },
      })
      .test({
        name: "maxFechaAtencionReagravacion",
        message:
          "La fecha de reagravación debe ser menor o igual a la fecha de atención de reagravación",
        test: function (value) {
          return this.parent.fechaAtencionReagravacion &&
            this.parent.reagravacion
            ? value <= this.parent.fechaAtencionReagravacion
            : true;
        },
      }),

    tipoReingreso: Yup.string()
      .nullable()
      .test({
        name: "required",
        message: "Campo obligatorio",
        test: function (value) {
          return this.parent.reagravacion ? value : true;
        },
      }),

    comoOcurrioAccidente: Yup.string().max(
      1000,
      "El campo no puede superar los 1000 caracteres",
    ),
    centroAsistencial: Yup.string().max(
      30,
      "El campo no puede superar los 30 caracteres",
    ),
    diagnostico: Yup.string().max(
      1200,
      "El campo no puede superar los 1200 caracteres",
    ),
    tratamiento: Yup.string().max(
      1500,
      "El campo no puede superar los 1500 caracteres",
    ),
    profesionalApellidoNombre: Yup.string()
      .nullable()
      .max(40, "El campo no puede superar los 40 caracteres"),
    profesionalMnMp: Yup.string().max(
      15,
      "El campo no puede superar los 15 caracteres",
    ),
  });

export const ietValidation = (fechaAtencionMinDate) =>
  Yup.object({
    fechaAtencion: Yup.date()
      .nullable()
      .typeError("Fecha inválida")
      .max(
        new Date(Date.now()),
        "La fecha hora de atención no puede ser mayor a la fecha actual",
      )
      .test({
        name: "maxFechaHoraNuevoControl",
        message:
          "La fecha de atención debe ser menor a la fecha de próxima consulta",
        test: function (value) {
          return this.parent.fechaHoraNuevoControl
            ? value < this.parent.fechaHoraNuevoControl
            : true;
        },
      })
      .test({
        name: "minFechaAtencion",
        message: `La fecha de atención debe ser mayor a la fecha de primera curación: ${moment(
          fechaAtencionMinDate,
        ).format("DD/MM/YYYY HH:mm")}`,
        test: function (value) {
          return fechaAtencionMinDate
            ? value > new Date(fechaAtencionMinDate)
            : true;
        },
      }),
    fechaProbableDeAlta: Yup.date()
      .nullable()
      .typeError("Fecha inválida")
      .when("representa", {
        is: (r) => {
          return r === "Internación";
        },
        then: () =>
          Yup.date()
            .nullable()
            .typeError("Fecha inválida")
            .required("Campo obligatorio")
            .test({
              name: "fechaProbableDeAltaValidation",
              message:
                "La fecha probable de alta debe ser mayor a la fecha de atención",
              test: function (value) {
                const { fechaAtencion } = this.parent;
                if (!fechaAtencion || !value) {
                  return true; // Permite validación si falta alguno de los valores
                }
                return value >= fechaAtencion; // Compara las fechas
              },
            }),
      })
      .when("representa", {
        is: (r) => {
          return r === "Externación";
        },
        then: () => Yup.date().nullable().typeError("Fecha inválida"),
      })
      .when("representa", {
        is: (r) => {
          return r === "Evolución Internación";
        },
        then: () =>
          Yup.date()
            .nullable()
            .required("Campo obligatorio")
            .typeError("Fecha inválida"),
      }),
    fechaProrrogaAlta: Yup.date()
      .nullable()
      .typeError("Fecha inválida")
      .test({
        name: "fechaProrrogaAltaValidation",
        message:
          "La fecha prórroga de alta no puede ser igual o anterior a la fecha probable de alta",
        test: function (value) {
          const { fechaProbableDeAlta } = this.parent;
          // Solo valida si fechaProrrogaAlta tiene un valor
          if (
            value &&
            fechaProbableDeAlta &&
            this.parent.representa === "Evolución Internación"
          ) {
            return value > fechaProbableDeAlta;
          }
          return true; // Permite valor nulo o indefinido, o fechaProbableDeAlta nula
        },
      }),
    fechaHoraNuevoControl: Yup.date()
      .nullable()
      .typeError("Fecha inválida")
      .min(
        new Date(Date.now()),
        "La fecha hora de próxima consulta no puede ser menor a la fecha actual",
      ),
    diagnostico: Yup.string().max(
      1200,
      "El campo no puede superar los 1200 caracteres",
    ),
    observaciones: Yup.string()
      .nullable()
      .max(6000, "El campo no puede superar los 6000 caracteres"),
    profesionalApellidoNombre: Yup.string()
      .nullable()
      .max(40, "El campo no puede superar los 40 caracteres"),
    profesionalMnMp: Yup.string().max(
      15,
      "El campo no puede superar los 15 caracteres",
    ),
  });

export const amValidation = (
  fechaAltaMinDate,
  configuration,
  diasReinicioLaboral,
) =>
  Yup.object({
    fechaAlta: Yup.date()
      .nullable()
      .typeError("Fecha inválida")
      .test({
        name: "minFechaAlta",
        message: `La fecha de alta debe ser mayor o igual a la fecha: ${moment(
          fechaAltaMinDate,
        ).format("DD/MM/YYYY")}`,
        test: function (value) {
          return fechaAltaMinDate ? value >= new Date(fechaAltaMinDate) : true;
        },
      })
      .test({
        name: "maxFechaAlta",
        message: "La fecha de alta debe ser menor o igual a la fecha actual",
        test: function (value) {
          return configuration.fechaAltaMax ? value <= new Date() : true;
        },
      }),
    fechaReinicioLaboral: Yup.date()
      .nullable()
      .typeError("Fecha inválida")
      .test({
        name: "minFechaReinicioLaboral",
        message:
          "La fecha de reinicio laboral debe ser mayor a la fecha de alta",
        test: function (value) {
          return value && this.parent.fechaAlta
            ? value > this.parent.fechaAlta
            : true;
        },
      })
      .test({
        name: "minFechaReinicioLaboralDefault",
        message: `La fecha de reinicio laboral debe ser mayor a la fecha de alta más ${diasReinicioLaboral} día${diasReinicioLaboral === 1 ? "" : "s"
          }`,
        test: function (value) {
          const fechaMin = moment(this.parent.fechaAlta).add(
            diasReinicioLaboral,
            "days",
          );
          return value &&
            configuration.fechaReinicioLaboralMin &&
            this.parent.fechaAlta
            ? moment(value).diff(fechaMin, "days") >= 0
            : true;
        },
      }),
    firmaConforme: Yup.string()
      .nullable()
      .test({
        name: "firmaConforme",
        message: "Campo obligatorio",
        test: function (value) {
          return (
            (this.parent.marcaFirmado === "Si" && value !== null) ||
            (this.parent.marcaFirmado === "No" && value === null)
          );
        },
      }),
    diagnostico: Yup.string().max(
      1200,
      "El campo no puede superar los 1200 caracteres",
    ),
    observaciones: Yup.string()
      .nullable()
      .max(1000, "El campo no puede superar los 1000 caracteres"),
    profesionalApellidoNombre: Yup.string()
      .nullable()
      .max(40, "El campo no puede superar los 40 caracteres")
      .required("obligatorio"),
    profesionalMnMp: Yup.string().max(
      15,
      "El campo no puede superar los 15 caracteres",
    ),
  });

export const fpaValidation = (fechaHoraPedidoMinDate) =>
  Yup.object({
    fechaHoraPedido: Yup.date()
      .nullable()
      .typeError("Fecha inválida")
      .test({
        name: "minFechaHoraPedido",
        message: `La fecha debe ser mayor o igual a la fecha: ${moment(
          fechaHoraPedidoMinDate,
        ).format("DD/MM/YYYY")}`,
        test: function (value) {
          return fechaHoraPedidoMinDate && value
            ? value >= new Date(fechaHoraPedidoMinDate)
            : true;
        },
      }),
    diagnostico: Yup.string().max(
      1200,
      "El campo no puede superar los 1200 caracteres",
    ),
    detalleAutorizacion: Yup.string()
      .nullable()
      .max(500, "El campo no puede superar los 500 caracteres"),
    planTerapeutico: Yup.string()
      .nullable()
      .max(500, "El campo no puede superar los 500 caracteres"),
    profesionalApellidoNombre: Yup.string()
      .nullable()
      .max(40, "El campo no puede superar los 40 caracteres"),
    profesionalMnMp: Yup.string().max(
      15,
      "El campo no puede superar los 15 caracteres",
    ),
  });

const hasPuntosRecorrido = (values) =>
  Array.isArray(values?.puntos) && values.puntos.length > 0;

export const FPT_LEGACY_ADDRESS_FIELDS = [
  "direccionHabitualDesde",
  "tipoDireccionDesde",
  "calleDesde",
  "numeroDesde",
  "pisoDesde",
  "departamentoDesde",
  "entreCalleDesde",
  "entreCalleDesde1",
  "localidadDesde",
  "provinciaDesde",
  "codigoPostalDesde",
  "telefonosDesde",
  "direccionHabitualHasta",
  "tipoDireccionHasta",
  "calleHasta",
  "numeroHasta",
  "pisoHasta",
  "departamentoHasta",
  "entreCalleHasta",
  "entreCalleHasta1",
  "localidadHasta",
  "provinciaHasta",
  "codigoPostalHasta",
  "telefonosHasta",
];

export const FPT_SKIP_REQUIRED_FIELDS = [
  "idAgencia",
  "idMotivoTraslado",
  "estados",
  "gestores",
  "origenPedido",
  "idModalidad",
  "idTipoTraslado",
  "idTipoSolicitante",
  "idEstadoInicialTraslados",
];

export const fptValidation = (fechaHoraSolicitudMinDate) =>
  Yup.object({
    fechaHoraSolicitud: Yup.date()
      .nullable()
      .typeError("Fecha inválida")
      .test({
        name: "minFechaHoraSolicitud",
        message: `La fecha debe ser mayor o igual a la fecha: ${moment(
          fechaHoraSolicitudMinDate,
        ).format("DD/MM/YYYY")}`,
        test: function (value) {
          return fechaHoraSolicitudMinDate && value
            ? value >= new Date(fechaHoraSolicitudMinDate)
            : true;
        },
      }),
    fechaHoraTurno: Yup.date()
      .nullable()
      .typeError("Fecha inválida")
      .test({
        name: "minfechaHoraTurno",
        message: "La fecha debe ser mayor a la fecha de solicitud",
        test: function (value) {
          return this.parent.fechaHoraSolicitud
            ? value > this.parent.fechaHoraSolicitud
            : true;
        },
      }),
    calleDesde: Yup.string()
      .max(50, "El campo no puede superar los 50 caracteres")
      .test({
        name: "sameAscalleHasta",
        message:
          "La calle y número Desde no pueden coincidir con la calle y número Hasta",
        test: function (value) {
          // En la modalidad con puntos el retorno puede repetir el origen.
          if (hasPuntosRecorrido(this.parent)) return true;
          return this.parent.calleHasta && this.parent.numeroHasta
            ? value !== this.parent.calleHasta ||
            this.parent.numeroDesde !== this.parent.numeroHasta
            : true;
        },
      }),
    pisoDesde: Yup.string().max(
      5,
      "El campo no puede superar los 5 caracteres",
    ),
    departamentoDesde: Yup.string().max(
      5,
      "El campo no puede superar los 5 caracteres",
    ),
    entreCalleDesde: Yup.string().max(
      50,
      "El campo no puede superar los 50 caracteres",
    ),
    entreCalleDesde1: Yup.string().max(
      50,
      "El campo no puede superar los 50 caracteres",
    ),
    localidadDesde: Yup.string().max(
      50,
      "El campo no puede superar los 50 caracteres",
    ),
    provinciaDesde: Yup.string().max(
      20,
      "El campo no puede superar los 20 caracteres",
    ),
    codigoPostalDesde: Yup.number()
      .transform((value, original) => (original === "" ? null : value))
      .nullable()
      .test({
        name: "codigoPostalDesde",
        message: "Campo obligatorio",
        test: function (value) {
          if (hasPuntosRecorrido(this.parent)) return true;
          return value != null && value !== 0;
        },
      }),
    telefonosDesde: Yup.string().max(
      20,
      "El campo no puede superar los 20 caracteres",
    ),
    calleHasta: Yup.string()
      .max(50, "El campo no puede superar los 50 caracteres")
      .test({
        name: "sameAscalleDesde",
        message:
          "La calle y número Hasta no pueden coincidir con la calle y número Desde",
        test: function (value) {
          if (hasPuntosRecorrido(this.parent)) return true;
          return this.parent.calleDesde && this.parent.numeroDesde
            ? value !== this.parent.calleDesde ||
            this.parent.numeroHasta !== this.parent.numeroDesde
            : true;
        },
      }),
    pisoHasta: Yup.string().max(
      5,
      "El campo no puede superar los 5 caracteres",
    ),
    departamentoHasta: Yup.string().max(
      5,
      "El campo no puede superar los 5 caracteres",
    ),
    entreCalleHasta: Yup.string().max(
      50,
      "El campo no puede superar los 50 caracteres",
    ),
    entreCalleHasta1: Yup.string().max(
      50,
      "El campo no puede superar los 50 caracteres",
    ),
    localidadHasta: Yup.string().max(
      50,
      "El campo no puede superar los 50 caracteres",
    ),
    provinciaHasta: Yup.string().max(
      20,
      "El campo no puede superar los 20 caracteres",
    ),
    codigoPostalHasta: Yup.number()
      .transform((value, original) => (original === "" ? null : value))
      .nullable()
      .test({
        name: "codigoPostalHasta",
        message: "Campo obligatorio",
        test: function (value) {
          if (hasPuntosRecorrido(this.parent)) return true;
          return value != null && value !== 0;
        },
      }),
    telefonosHasta: Yup.string().max(
      20,
      "El campo no puede superar los 20 caracteres",
    ),
    observaciones: Yup.string().max(
      500,
      "El campo no puede superar los 500 caracteres",
    ),
    idModalidad: Yup.mixed().test({
      name: "idModalidadRequired",
      message: "Campo obligatorio",
      test(value) {
        if (!hasPuntosRecorrido(this.parent)) return true;
        return value !== "" && value != null;
      },
    }),
    idTipoTraslado: Yup.mixed().test({
      name: "idTipoTrasladoRequired",
      message: "Campo obligatorio",
      test(value) {
        if (!hasPuntosRecorrido(this.parent)) return true;
        return value !== "" && value != null;
      },
    }),
    idTipoSolicitante: Yup.mixed().test({
      name: "idTipoSolicitanteRequired",
      message: "Campo obligatorio",
      test(value) {
        if (!hasPuntosRecorrido(this.parent)) return true;
        return value !== "" && value != null;
      },
    }),
    puntos: Yup.array()
      .test({
        name: "puntosConsecutivos",
        message: "Dos puntos consecutivos no pueden tener la misma dirección.",
        test(puntos) {
          if (!puntos || puntos.length < 2) return true;
          const first = puntos[0];
          for (let index = 0; index < puntos.length - 1; index += 1) {
            if (!samePuntoAddress(puntos[index], puntos[index + 1])) continue;
            const isAutoReturn =
              Boolean(puntos[index + 1].esRetorno) &&
              samePuntoAddress(puntos[index + 1], first);
            if (!isAutoReturn) return false;
          }
          return true;
        },
      })
      .test({
        name: "puntosFechas",
        message:
          'Las fechas de los tramos deben ser iguales o posteriores a "Para el" y ascendentes entre sí.',
        test(puntos) {
          if (!puntos?.length) return true;
          const minTurno = this.parent.fechaHoraTurno
            ? new Date(this.parent.fechaHoraTurno).getTime()
            : null;

          for (let index = 0; index < puntos.length; index += 1) {
            const fecha = puntos[index]?.fechaHora
              ? new Date(puntos[index].fechaHora).getTime()
              : null;
            if (!fecha) continue;

            if (minTurno != null && fecha < minTurno) return false;

            if (index > 0) {
              const prevFecha = puntos[index - 1]?.fechaHora
                ? new Date(puntos[index - 1].fechaHora).getTime()
                : null;
              if (prevFecha != null && fecha <= prevFecha) return false;
            }
          }

          return true;
        },
      }),
  });

export const siniestroValidation = (fromFacade, isEditMode, fechaHoraAbandonoTrabajo) =>
  Yup.object({
    fechaHoraAccidente: Yup.date()
      .nullable()
      .typeError("Fecha inválida")
      .max(
        new Date(Date.now()),
        "La fecha hora de accidente no puede ser mayor a la fecha actual",
      ),
    fechaHoraAbandonoTrabajo: Yup.date()
      .nullable()
      .typeError("Fecha inválida")
      .when([], {
        is: () => !(fromFacade && !isEditMode && !!fechaHoraAbandonoTrabajo),
        then: (schema) => schema.max(
          new Date(Date.now()),
          "La fecha de abandono de trabajo no puede ser mayor a la fecha actual",
        )
          .test({
            name: "minFechaHoraAccidente",
            message:
              "La fecha en que abandonó el trabajo debe ser mayor o igual a la fecha de accidente",
            test: function (value) {
              return this.parent.fechaHoraAccidente && value
                ? value >= this.parent.fechaHoraAccidente
                : true;
            },
          }),
        otherwise: (schema) => schema,
      }),
    nroSiniestroArt: Yup.string().max(
      15,
      "El campo no puede superar los 15 caracteres",
    ),
    numeroDocumentoTrabajador: Yup.number()
      .integer("Número de documento inválido")
      .max(99999999, "El campo no puede superar los 8 dígitos"),
    apellidoTrabajador: Yup.string().max(
      40,
      "El campo no puede superar los 40 caracteres",
    ),
    nombreTrabajador: Yup.string().max(
      40,
      "El campo no puede superar los 40 caracteres",
    ),
    fechaNacimientoTrabajador: Yup.date()
      .nullable()
      .typeError("Fecha inválida")
      .max(
        new Date(Date.now()),
        "La fecha de nacimiento no puede ser mayor a la fecha actual",
      ),
    cuilCuitTrabajador: Yup.string()
      .max(13, "El campo no puede superar los 13 caracteres")
      .test({
        name: "cuilFormat",
        message: "Cuil inválido",
        test: function (value) {
          return !value || fromFacade
            ? true
            : value.match(/^([0-9]{2}-[0-9]{8}-[0-9]{1})$/);
        },
      }),
    calleTrabajador: Yup.string().max(
      50,
      "El campo no puede superar los 50 caracteres",
    ),
    pisoTrabajador: Yup.string().max(
      5,
      "El campo no puede superar los 5 caracteres",
    ),
    departamentoTrabajador: Yup.string().max(
      5,
      "El campo no puede superar los 5 caracteres",
    ),
    localidadTrabajador: Yup.string().max(
      50,
      "El campo no puede superar los 50 caracteres",
    ),
    provinciaTrabajador: Yup.string().max(
      20,
      "El campo no puede superar los 20 caracteres",
    ),
    telefonosTrabajador: Yup.string().max(
      20,
      "El campo no puede superar los 20 caracteres",
    ),
    areaTelefonoCelularTrabajador: Yup.string().max(
      5,
      "El campo no puede superar los 5 caracteres",
    ),
    telefonoCelularTrabajador: Yup.string().max(
      30,
      "El campo no puede superar los 30 caracteres",
    ),
    comoOcurrioAccidente: Yup.string().max(
      1000,
      "El campo no puede superar los 1000 caracteres",
    ),
    cuitEmpleador: Yup.string()
      .max(13, "El campo no puede superar los 13 caracteres")
      .test({
        name: "cuitFormat",
        message: "Cuit inválido",
        test: function (value) {
          return !value || fromFacade
            ? true
            : value.match(/^([0-9]{2}-[0-9]{8}-[0-9]{1})$/);
        },
      }),
    razonSocialEmpleador: Yup.string().max(
      50,
      "El campo no puede superar los 50 caracteres",
    ),
    calleEmpleador: Yup.string().max(
      50,
      "El campo no puede superar los 50 caracteres",
    ),
    pisoEmpleador: Yup.string().max(
      5,
      "El campo no puede superar los 5 caracteres",
    ),
    departamentoEmpleador: Yup.string().max(
      5,
      "El campo no puede superar los 5 caracteres",
    ),
    localidadEmpleador: Yup.string().max(
      50,
      "El campo no puede superar los 50 caracteres",
    ),
    provinciaEmpleador: Yup.string().max(
      20,
      "El campo no puede superar los 20 caracteres",
    ),
    telefonosEmpleador: Yup.string().max(
      20,
      "El campo no puede superar los 20 caracteres",
    ),
  });

export const idrValidation = () =>
  Yup.object({
    detalle: Yup.string()
      .nullable()
      .max(6000, "El campo no puede superar los 6000 caracteres"),
    profesionalApellidoNombre: Yup.string()
      .nullable()
      .max(40, "El campo no puede superar los 40 caracteres"),
    profesionalMnMp: Yup.string().max(
      15,
      "El campo no puede superar los 15 caracteres",
    ),
  });

export const validateRequiredFields = (values, model, skipFields = []) => {
  const errors = {};
  const errorMessage = "Campo obligatorio";
  const skipped = new Set(skipFields);

  for (var field in model) {
    if (skipped.has(field)) continue;
    if (model[field].required && !values[field] && values[field] !== 0 && values[field] !== false) {
      errors[field] = errorMessage;
    }
  }

  return errors;
};
